import { useEffect, useState } from "react";
import produce from "immer";
import set from "lodash.set";

import Routing from "./Routing";
import { STEP_STORAGE_KEYS } from "./constants/storageConstants";
import { useCurrentProjectKey } from "./util/projectDataHooks";
import { NEW_PROJECT_KEY, PROJECT_KEY } from "./constants/storageConstants";

const getProjectDataByKey = ({ key }) =>
  JSON.parse(sessionStorage.getItem(key)) || {};

export default function Main() {
  const [currentProjectKey, setCurrentProjectKey] = useCurrentProjectKey();

  const [projectData, setProjectData] = useState(
    getProjectDataByKey({ key: currentProjectKey })
  );

  /**
   * @param {String} freshProjectKey the key of the project that the app is switching to.
   * For example: NEW_PROJECT_PENN_DOT_ICE, or PROJECT_DATA_PENN_DOT_ICE-1673479084438
   * @param {Object} [freshProjectData] project form data associated with the new project key
   */
  const setCurrentProject = ({ freshProjectKey, freshProjectData }) => {
    setCurrentProjectKey(freshProjectKey);
    setProjectData(
      freshProjectData || getProjectDataByKey({ key: freshProjectKey })
    );
  };

  /**
   * @param {String} paramString something like "trafficVolumes.openingYear.amPeak" or "projectInfo"
   * @returns true if the paramString is one from the "projectInfo" step and we are starting a new project
   */
  const isInitializingNewProject = ({ paramString }) =>
    paramString?.startsWith(STEP_STORAGE_KEYS.PROJECT_INFORMATION) &&
    currentProjectKey === NEW_PROJECT_KEY;

  /**
   * @param {String} paramString string like "trafficVolumes.designYear.amPeak.truckPct" that lets us
   * set a value in an object by passing the dot-separated path to that spot in the object
   * @param {any} value value being saved
   */
  const saveFormData = ({ paramString, value }) => {
    const projectInfoPath = STEP_STORAGE_KEYS.PROJECT_INFORMATION;
    const timestamp = Date.now();

    if (isInitializingNewProject({ paramString })) {
      setCurrentProjectKey(`${PROJECT_KEY}-${Date.now()}`);
    }

    setProjectData((prevState = {}) => {
      return produce(prevState, (draft) => {
        // save the current form's values in projectData
        draft = set(draft, paramString, value);

        // set the updatedDate timestamp any time we save a value to projectData
        draft = set(draft, `${projectInfoPath}.updatedDate`, timestamp);

        // if it's a new analysis, set the createdDate
        if (isInitializingNewProject({ paramString })) {
          draft = set(draft, `${projectInfoPath}.createdDate`, timestamp);
        }
      });
    });
  };

  // update the project data in sessionStorage each time the projectData state is updated
  useEffect(() => {
    if (
      currentProjectKey &&
      currentProjectKey !== NEW_PROJECT_KEY &&
      projectData
    ) {
      sessionStorage.setItem(currentProjectKey, JSON.stringify(projectData));
    }
  }, [currentProjectKey, projectData]);

  return (
    <Routing
      projectData={projectData}
      onNewProjectStart={() =>
        setCurrentProject({ freshProjectKey: NEW_PROJECT_KEY })
      }
      saveFormData={saveFormData}
      setCurrentProject={setCurrentProject}
    />
  );
}
